/**
 * TimelineComputedProvider
 *
 * Lightweight wrapper that adds computed hook results to timeline context.
 * Does NOT create a reducer - only merges additional values.
 *
 * This provider is used inside CropTimelineInner to add zoom/scroll hook
 * results to the context after they're computed.
 *
 * Architecture:
 * - TimelineContextProvider (creates reducer) in CropTimelineWithContext
 * - TimelineComputedProvider (adds hook results) in CropTimelineInner
 *
 * Extends both split contexts:
 * - TimelineStaticContext: receives all computed layout values
 *   (TIMELINE_WIDTH, NOW_X, CUTOFF_X, FROST_DATES, getRowY, etc.)
 * - TimelineInteractionContext: no additional fields added here
 */

import { useMemo } from 'react'
import { useTimelineContextDispatch } from './timeline-context'
import { TimelineContextDispatch } from './timeline-context'
import { TimelineStaticContext, useTimelineStaticContext } from './timeline-static-context'
import { TimelineInteractionContext, useTimelineInteractionContext } from './timeline-interaction-context'

export const TimelineComputedProvider = ({ children, computedValues = {} }) => {
  const existingDispatch = useTimelineContextDispatch()
  const existingStatic = useTimelineStaticContext()
  const existingInteraction = useTimelineInteractionContext()

  // Static: extend with all computed layout values (TIMELINE_WIDTH, NOW_X, CUTOFF_X, getRowY, etc.)
  const mergedStatic = useMemo(() => ({
    ...existingStatic,
    ...computedValues,
  }), [existingStatic, computedValues])

  // Interaction: no additional fields from computed values
  const mergedInteraction = useMemo(() => existingInteraction, [existingInteraction])

  return (
    <TimelineStaticContext.Provider value={mergedStatic}>
      <TimelineInteractionContext.Provider value={mergedInteraction}>
        <TimelineContextDispatch.Provider value={existingDispatch}>
          {children}
        </TimelineContextDispatch.Provider>
      </TimelineInteractionContext.Provider>
    </TimelineStaticContext.Provider>
  )
}
